import { create } from 'zustand';
import { AuditLog, AuditFilters, PaginationInfo, SystemStats } from '../../types/super-admin';

interface AuditState {
  // Data
  logs: AuditLog[];
  stats: SystemStats;
  
  // Filters and pagination
  filters: AuditFilters;
  pagination: PaginationInfo;
  
  // UI state
  loading: boolean;
  error: string | null;
  
  // Export state
  exporting: boolean;
  exportError: string | null;
  
  // Actions
  setLogs: (logs: AuditLog[]) => void;
  addLog: (log: AuditLog) => void;
  setStats: (stats: SystemStats) => void;
  
  setFilters: (filters: Partial<AuditFilters>) => void;
  resetFilters: () => void;
  
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  setExporting: (exporting: boolean) => void;
  setExportError: (error: string | null) => void;
  
  // Pagination
  setPagination: (pagination: PaginationInfo) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  
  // Helper methods
  getLogById: (logId: string) => AuditLog | undefined;
  getLogsByCategory: (category: string) => AuditLog[];
  getLogsBySeverity: (severity: string) => AuditLog[];
}

const initialFilters: AuditFilters = {
  search: '',
  category: '',
  severity: '',
  userId: '',
  tenantId: '',
  dateFrom: '',
  dateTo: '',
  sortBy: 'timestamp',
  sortOrder: 'desc'
};

const initialPagination: PaginationInfo = {
  page: 1,
  limit: 50,
  total: 0,
  totalPages: 0,
  hasNext: false,
  hasPrev: false
};

const initialStats: SystemStats = {
  totalUsers: 0,
  totalTenants: 0,
  totalContent: 0,
  totalCampaigns: 0,
  activeUsers: 0,
  activeTenants: 0,
  usersByRole: {},
  usersByTenant: {},
  tenantsByTier: {},
  recentActivity: {
    newUsers: 0,
    newTenants: 0,
    loginAttempts: 0,
    failedLogins: 0
  }
};

export const useAuditStore = create<AuditState>((set, get) => ({
  // Initial state
  logs: [],
  stats: initialStats,
  filters: initialFilters,
  pagination: initialPagination,
  loading: false,
  error: null,
  exporting: false,
  exportError: null,
  
  // Actions
  setLogs: (logs: AuditLog[]) => {
    set({ logs, error: null });
  },
  
  addLog: (log: AuditLog) => {
    set(state => ({
      logs: [log, ...state.logs]
    }));
  },
  
  setStats: (stats: SystemStats) => {
    set({ stats });
  },
  
  setFilters: (newFilters: Partial<AuditFilters>) => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 1 } // Reset to first page when filters change
    }));
  },
  
  resetFilters: () => {
    set({
      filters: initialFilters,
      pagination: { ...initialPagination }
    });
  },
  
  setLoading: (loading: boolean) => {
    set({ loading });
  },
  
  setError: (error: string | null) => {
    set({ error });
  },
  
  setExporting: (exporting: boolean) => {
    set({ exporting });
  },
  
  setExportError: (error: string | null) => {
    set({ exportError: error });
  },
  
  setPagination: (pagination: PaginationInfo) => {
    set({ pagination });
  },
  
  setPage: (page: number) => {
    set(state => ({
      pagination: { ...state.pagination, page }
    }));
  },
  
  setLimit: (limit: number) => {
    set(state => ({
      pagination: { ...state.pagination, limit, page: 1 }
    }));
  },
  
  // Helper methods
  getLogById: (logId: string) => {
    const state = get();
    return state.logs.find(log => log.id === logId);
  },
  
  getLogsByCategory: (category: string) => {
    const state = get();
    return state.logs.filter(log => log.category === category);
  },
  
  getLogsBySeverity: (severity: string) => {
    const state = get();
    return state.logs.filter(log => log.severity === severity);
  }
}));